import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WorkflowCanvas Component
 * Main visual canvas for building workflows with React Flow
 * Features: Drag-and-drop nodes, real-time sync with Zustand, keyboard shortcuts
 */
import React, { useCallback } from 'react';
import { ReactFlow, addEdge, Background, Controls, MiniMap, applyNodeChanges, applyEdgeChanges, useReactFlow, MarkerType, } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useWorkflowStore } from '@/store/workflow.store';
import { TriggerNode } from './nodes/TriggerNode';
import { ActionNode } from './nodes/ActionNode';
import { ConditionNode } from './nodes/ConditionNode';
import { NodeSidebar } from './NodeSidebar';
import { v4 as uuid } from 'uuid';
// Memoized node types to prevent re-renders
const nodeTypes = {
    trigger: React.memo(TriggerNode),
    action: React.memo(ActionNode),
    condition: React.memo(ConditionNode),
};
export const WorkflowCanvas = ({ onNodeDataChange }) => {
    // Get React Flow instance
    const { getNode, getEdges, setCenter } = useReactFlow();
    // Zustand store
    const { canvasNodes, canvasEdges, setCanvasNodes, setCanvasEdges, setCanvasState, canvasState, } = useWorkflowStore();
    /**
     * PART 9: EXECUTION ENGINE INTEGRATION TODOs
     * ============================================
     *
     * 1. Add execution state tracking:
     *    - Add useWorkflowStore state: executionState { isRunning, currentNodeId, progress, logs[] }
     *    - Track which node is currently executing (highlight in canvas)
     *    - Display execution logs in real-time panel below canvas
     *
     * 2. Workflow validation & compilation:
     *    - validateWorkflow(): Check all nodes have required config, edges are valid
     *    - compileWorkflowDefinition(): Convert canvas nodes/edges to execution-ready format
     *    - Show validation errors to user before execution
     *
     * 3. Add execution control buttons:
     *    - Execute button (validates, starts workflow execution)
     *    - Stop/Pause buttons (cancel running execution)
     *    - Clear logs button
     *    - Test trigger simulation (fire manual triggers)
     *
     * 4. Real-time execution visualization:
     *    - Highlight current executing node with animated border/glow
     *    - Show edge progress as animation flowing through connections
     *    - Display execution status in node tooltip (queued/running/completed/failed)
     *    - Log panel with step-by-step execution details
     *
     * 5. Integration points:
     *    - Call workflows.service.executeWorkflow(workflowId, canvasDefinition)
     *    - Subscribe to real-time execution updates via Supabase
     *    - Update canvasState with executionState for visualization
     *    - Display errors/warnings in overlay when execution fails
     *
     * 6. Error handling & debugging:
     *    - Catch node execution errors, show in UI with stack trace
     *    - Timeout detection for long-running actions
     *    - Retry button for failed steps
     *    - Export execution logs as JSON for debugging
     */
    // Handle node changes (drag, select, etc.)
    const onNodesChange = useCallback((changes) => {
        const updatedNodes = applyNodeChanges(changes, canvasNodes);
        setCanvasNodes(updatedNodes);
    }, [canvasNodes, setCanvasNodes]);
    // Handle edge changes (delete, etc.)
    const onEdgesChange = useCallback((changes) => {
        const updatedEdges = applyEdgeChanges(changes, canvasEdges);
        setCanvasEdges(updatedEdges);
    }, [canvasEdges, setCanvasEdges]);
    // Handle new connections
    const onConnect = useCallback((connection) => {
        const newEdges = addEdge({
            ...connection,
            animated: true,
            markerEnd: { type: MarkerType.ArrowClosed },
        }, canvasEdges);
        setCanvasEdges(newEdges);
    }, [canvasEdges, setCanvasEdges]);
    // Handle drag and drop from sidebar
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);
    const onDrop = useCallback((event) => {
        event.preventDefault();
        const nodeData = event.dataTransfer.getData('application/reactflow');
        if (!nodeData)
            return;
        try {
            const { type, defaultData } = JSON.parse(nodeData);
            const { project } = useReactFlow();
            // Get the drop position relative to canvas
            const reactFlowBounds = event.currentTarget.getBoundingClientRect();
            const clientPosition = {
                x: event.clientX - reactFlowBounds.left,
                y: event.clientY - reactFlowBounds.top,
            };
            // Project screen coordinates to flow coordinates
            const position = project(clientPosition);
            // Create new node
            const newNode = {
                id: `${type}-${uuid()}`,
                type,
                position,
                data: defaultData,
                selected: true,
            };
            // Add to canvas
            setCanvasNodes([...canvasNodes, newNode]);
        }
        catch (error) {
            console.error('Error adding node:', error);
        }
    }, [canvasNodes, setCanvasNodes, useReactFlow]);
    // Handle zoom to fit all nodes
    const fitView = useCallback(() => {
        if (canvasNodes.length > 0) {
            const bounds = {
                minX: Math.min(...canvasNodes.map(n => n.position.x)),
                minY: Math.min(...canvasNodes.map(n => n.position.y)),
                maxX: Math.max(...canvasNodes.map(n => n.position.x + 200)),
                maxY: Math.max(...canvasNodes.map(n => n.position.y + 100)),
            };
            const width = bounds.maxX - bounds.minX;
            const height = bounds.maxY - bounds.minY;
            const x = bounds.minX + width / 2;
            const y = bounds.minY + height / 2;
            setCenter(x, y, { zoom: 1, duration: 200 });
        }
    }, [canvasNodes, setCenter]);
    // Keyboard shortcuts
    React.useEffect(() => {
        const handleKeyDown = (event) => {
            // Fit view with 'f' key
            if (event.key === 'f') {
                event.preventDefault();
                fitView();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [fitView]);
    return (_jsxs("div", { className: "flex gap-4 h-full bg-gray-50 dark:bg-gray-900", children: [_jsx(NodeSidebar, {}), _jsxs("div", { className: "flex-1 relative", onDragOver: onDragOver, onDrop: onDrop, children: [_jsxs(ReactFlow, { nodes: canvasNodes, edges: canvasEdges, onNodesChange: onNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, nodeTypes: nodeTypes, fitView: true, deleteKeyCode: ['Delete', 'Backspace'], children: [_jsx(Background, { color: "#cbd5e1", style: {
                                    backgroundColor: 'transparent',
                                }, variant: "dots", gap: 20, size: 1 }), _jsx(Controls, { showFitView: true, showZoom: true, showInteractive: true, position: "bottom-left" }), _jsx(MiniMap, { position: "bottom-right", className: "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg", nodeColor: (node) => {
                                    if (node.type === 'trigger')
                                        return '#3b82f6';
                                    if (node.type === 'action')
                                        return '#10b981';
                                    if (node.type === 'condition')
                                        return '#f59e0b';
                                    return '#6b7280';
                                } })] }), _jsxs("div", { className: "absolute top-4 right-4 text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 pointer-events-none", children: ["Press ", _jsx("kbd", { className: "font-mono font-semibold", children: "f" }), " to fit view"] })] })] }));
};
export default WorkflowCanvas;
